import { invoke } from '@shared/easyfy.decorators';
import { globalEvent } from '@shared/easyfy.core';
import { addCss, removeCss } from '@shared/easyfy.utils';
import { PostForm } from '@clientWebServices/form.service';
import { replaceContent } from './utils';

@invoke
export default class Form {
  static Instance: Form;

  constructor() {
    Form.Instance = this;
    globalEvent({
      type: 'submit',
      events: [
        {
          action: 'haypp-ajax-form',
          callback: this.onPostHayppForm,
        },
      ],
    });
    globalEvent({
      type: 'click',
      events: [
        {
          event: 'toggle-password',
          callback: this.onTogglePassword,
        },
      ],
    });
    globalEvent({
      type: 'input',
      events: [
        {
          event: 'input-password',
          callback: this.onPasswordInput,
        },
        {
          event: 'confirm-password',
          callback: this.onConfirmPasswordInput,
        },
      ],
    });
  }

  onPostHayppForm(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    const element: HTMLFormElement = e.target as HTMLFormElement;
    const data: FormData = new FormData(element);
    const tokenEl: HTMLElement = element.closest('[data-token]') as HTMLElement;
    const token: string =
      tokenEl && tokenEl.dataset?.token
        ? tokenEl.dataset?.token
        : (document.querySelector('[name="__RequestVerificationToken"]') as HTMLInputElement)?.value ?? '';

    PostForm(element.attributes['action']?.value, data, token).then((response) => {
      if (response.data.success) {
        if (Form.Instance.getReturnUrl()) return (window.location.href = Form.Instance.getReturnUrl());

        if (response.data.partials) {
          for (const [key, value] of Object.entries(response.data.partials)) {
            const container = document.querySelector(`[data-container="${key}"]`);
            if (container) replaceContent(container, value as string, 'replaceInnerHtml');
          }
        } else if (response.data.reload) {
          setTimeout(function () {
            location.reload();
          }, 700);
        } else if (response.data.clearForm) {
          element.reset();
          if (response.data.partial) {
            Form.Instance.processPartial(response.data.partial);
          }
        } else {
          if (response.data.partial) {
            Form.Instance.processPartial(response.data.partial);
          }
        }
      } else {
        const errorMessages = response?.data?.errorMessages;
        if (element && response.data.generalErrorMessage) Form.Instance.setValidationSummary(element, response.data.generalErrorMessage?.value);
        if (element) Form.Instance.resetErrorMessages(element);
        if (errorMessages) Form.Instance.setErrorMessages(element, errorMessages);
      }
    });

    return false;
  }

  private getReturnUrl = (): string => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    return params?.get('ReturnUrl');
  };

  onTogglePassword = (e: Event) => {
    const el = e.target as HTMLInputElement;
    const pwdEl = el.closest('.form-group') as HTMLElement;
    const pwdInput = pwdEl?.querySelector('input') as HTMLInputElement;
    const showIcon = pwdEl?.querySelector('i') as HTMLElement;
    if (!pwdInput) return;
    if (pwdInput.type == 'password') {
      pwdInput.type = 'text';
      showIcon?.classList.remove('icon-show-true');
      showIcon?.classList.add('icon-show-false');
    } else {
      pwdInput.type = 'password';
      showIcon?.classList.remove('icon-show-false');
      showIcon?.classList.add('icon-show-true');
    }
  };

  onPasswordInput = (e: Event) => {
    const inputEl = e?.target as HTMLInputElement;
    const value = inputEl.value;

    this.updateRequirement('length', value.length >= 8);
    this.updateRequirement('lowercase', /[a-z]/.test(value));
    this.updateRequirement('uppercase', /[A-Z]/.test(value));
    this.updateRequirement('number', /\d/.test(value));
    this.updateRequirement('characters', /[!@#$%^&*_+=,.?-]/.test(value));

    this.handleFormValidation(inputEl.closest('form') as HTMLFormElement);
  };

  onConfirmPasswordInput = (e: Event) => {
    const confirmPassword = e?.target as HTMLInputElement;
    const form = confirmPassword.closest('form') as HTMLFormElement;
    const password = form.querySelector('input[name="newpassword"]') as HTMLInputElement;

    if (confirmPassword && password) {
      this.updateRequirement('match', confirmPassword.value == password.value);
      this.handleFormValidation(form);
      return true;
    }
    this.handleFormValidation(form);
    return false;
  };

  processPartial = (dataPartial: any) => {
    for (const [key, value] of Object.entries(dataPartial)) {
      const container = document.querySelector(`[data-container="${key}"]`);
      if (container) replaceContent(container, value as string, 'append', false);
    }
  };

  resetErrorMessages = (element: HTMLElement) => {
    element.querySelectorAll('.error')?.forEach((el) => {
      removeCss(el, 'error');
      if (el.querySelector('[data-container="error-message"')) el.querySelector('[data-container="error-message"').innerHTML = '';
    });
  };

  setErrorMessages = (element: HTMLElement, errorMessages) => {
    errorMessages?.forEach((error) => {
      const propertyName = error?.propertyName?.toLowerCase();
      const inputContainer = element.querySelector(`[data-container="form-${propertyName}"]`) as HTMLElement;
      const messageContainer = inputContainer.querySelector('[data-container="error-message"');
      const errorMessage = error?.errorMessage;
      if (messageContainer && errorMessage) {
        messageContainer.innerHTML = errorMessage;
        addCss(inputContainer, 'error');
      }
    });
  };

  setValidationSummary = (element: HTMLElement, message: string) => {
    const validationSummaryContainer = element.querySelector('[data-container="validation-summary"');
    if (validationSummaryContainer) validationSummaryContainer.innerHTML = message;
  };

  handleFormValidation = (form: HTMLFormElement) => {
    if (!form) return false;

    const validationErrors = form.querySelector('.password-validation-errors');
    const password = form.querySelector('input[name="newpassword"]') as HTMLInputElement;
    const confirmPassword = form.querySelector('input[name="confirmpassword"]') as HTMLInputElement;
    const submit = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const isFormValid = validationErrors?.querySelectorAll('.requirement:not(.hidden)').length === 0;

    if (password.value && confirmPassword.value && isFormValid) {
      submit.removeAttribute('disabled');
      return true;
    }

    submit.setAttribute('disabled', 'disabled');
    return false;
  };

  updateRequirement = (id: string, valid: boolean) => {
    const requirement = document.getElementById(id);
    if (requirement) {
      if (valid) {
        requirement.classList.add('hidden');
      } else {
        requirement.classList.remove('hidden');
      }
    }
  };
}
